/**
 * English strings for the `auth` namespace. The source of truth: French is
 * typed against this file. See `apps/web/src/i18n/README.md`.
 */
const auth = {
  signIn: {
    subtitle: {
      password: "Sign in to your workspace",
      totp: "Enter your authentication code",
      reset: "We'll email you a reset link",
      resetSent: "Check your inbox",
    },
    email: "Email",
    password: "Password",
    submit: "Sign in",
    forgotPassword: "Forgot your password?",
    sendResetLink: "Send reset link",
    backToSignIn: "Back to sign in",
    resetSent:
      "If an account exists for <email>{{email}}</email>, a reset link is on its way. The link expires in an hour.",
    totp: {
      appPrompt: "Open your authenticator app",
      backupPrompt: "Enter one of your backup codes",
      codeLabel: "6-digit code",
      backupLabel: "Backup code",
      verify: "Verify",
      useBackupCode: "Use a backup code",
      useApp: "Use your authenticator app",
    },
  },
  password: {
    moreNeeded_one: "{{count}} more character needed.",
    moreNeeded_other: "{{count}} more characters needed.",
  },
  reset: {
    incompleteTitle: "This link is incomplete",
    incompleteBody:
      "It is missing the part that proves it came from us. Request a new reset email and follow the link from there without editing it.",
    backToSignIn: "Back to sign in",
    doneTitle: "Password changed",
    doneBody: "Sign in with your new password.",
    goToSignIn: "Go to sign in",
    title: "Choose a new password",
    hint: "At least 12 characters. Everywhere you are signed in stays signed in.",
    newPassword: "New password",
    confirm: "Confirm it",
    mismatch: "These do not match.",
    submit: "Change password",
  },
  verify: {
    working: "Confirming your email…",
    doneTitle: "Email confirmed",
    doneBody: "Thanks — that is all we needed.",
    continue: "Continue",
    failedTitle: "This link did not work",
    failedBody:
      "It may have expired or already been used. You can carry on using the app either way — confirming your email is not required to sign in.",
    goToApp: "Go to the app",
  },
  accept: {
    checking: "Checking your invitation…",
    invalidTitle: "This invitation link is not valid",
    invalidBody:
      "It may have been mistyped, or revoked. Ask whoever invited you to send a new one.",
    usedTitle: "This invitation has already been used",
    usedBody: "If that was you, sign in instead.",
    goToSignIn: "Go to sign in",
    expiredTitle: "This invitation has expired",
    expiredBody:
      "Invitations are valid for a limited time. Ask for a new one and it will work straight away.",
    platformTitle: "Set up your workspace",
    joinTitle: "Join {{name}}",
    platformIntro:
      "Your meetings get recorded, transcribed and searchable. Pick a name for your workspace — you can invite your colleagues once you are in.",
    joinIntro: "You have been invited to {{name}}. Set a password to join.",
    email: "Email",
    name: "Your name",
    namePlaceholder: "Amin Aimeur",
    workspaceName: "Workspace name",
    workspacePlaceholder: "Acme Inc",
    workspaceHint: "Usually your company or team name. You can change it later.",
    password: "Password",
    passwordPlaceholder: "At least 12 characters",
    genericError: "Something went wrong. Try again.",
    createWorkspace: "Create my workspace",
    join: "Join {{name}}",
  },
  /**
   * Better Auth's error codes, as sentences. A wrong email and a wrong
   * password read the same on purpose — see `authErrorMessage`.
   */
  errors: {
    invalidCredentials: "That email and password don't match.",
    invalidPassword: "That password isn't right.",
    invalidEmail: "That doesn't look like an email address.",
    emailNotVerified: "Confirm your email address first — the link is in your inbox.",
    invalidCode: "That code isn't right. Try the next one.",
    invalidBackupCode: "That backup code isn't right, or it has already been used.",
    tooManyAttempts: "Too many attempts. Wait a few minutes, then try again.",
    twoFactorExpired: "This sign-in took too long. Enter your email and password again.",
    passwordTooShort: "Passwords need at least 12 characters.",
    passwordTooLong: "That password is too long.",
    invalidToken: "That link has expired. Request a new one.",
    userExists: "An account with that email already exists.",
    rateLimited: "Too many requests. Wait a moment and try again.",
    generic: "Something went wrong. Please try again.",
  },
} as const;

export default auth;
